import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Brain, 
  TrendingUp, 
  Users, 
  Building2, 
  Globe, 
  Loader2, 
  AlertCircle, 
  Settings, 
  BarChart3,
  Target,
  Calendar,
  DollarSign,
  MessageSquare,
  Clock,
  FileText,
  CheckCircle2,
  Filter,
  Download,
  Share2,
  Bookmark
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface AnalysisResult {
  analysis: string;
  dataCount: number;
  timestamp: string;
  error?: string;
}

interface AnalysisConfig {
  dataSource: string;
  analysisType: string;
  timeRange: string;
  filters: Record<string, any>;
  includeRecommendations: boolean;
  includeCharts: boolean;
  language: 'ar' | 'en';
  detailLevel: 'basic' | 'detailed' | 'comprehensive';
  customPrompt: string;
}

export default function AIAnalyticsPage() {
  const { toast } = useToast();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [savedConfigs, setSavedConfigs] = useState<AnalysisConfig[]>([]);
  
  const [config, setConfig] = useState<AnalysisConfig>({
    dataSource: '',
    analysisType: '',
    timeRange: 'last_30_days',
    filters: {},
    includeRecommendations: true,
    includeCharts: false,
    language: 'ar',
    detailLevel: 'detailed',
    customPrompt: ''
  });

  // مصادر البيانات المتاحة مع تفاصيل شاملة
  const dataSourceOptions = [
    { 
      value: 'clients', 
      label: 'العملاء', 
      icon: Users,
      description: 'تحليل بيانات العملاء والحالات والتحويل',
      fields: ['الاسم', 'الحالة', 'الشركة', 'المحمول', 'تاريخ الإنشاء']
    },
    { 
      value: 'employees', 
      label: 'الموظفين', 
      icon: Users,
      description: 'تحليل أداء الموظفين والحضور والإنتاجية',
      fields: ['الاسم', 'المنصب', 'القسم', 'الحالة', 'تاريخ التوظيف']
    },
    { 
      value: 'tasks', 
      label: 'المهام', 
      icon: CheckCircle2,
      description: 'تحليل إنجاز المهام والأولويات والتأخيرات',
      fields: ['العنوان', 'الحالة', 'الأولوية', 'الموعد النهائي', 'المُكلف']
    },
    { 
      value: 'bookings', 
      label: 'الحجوزات', 
      icon: Calendar,
      description: 'تحليل اتجاهات الحجوزات والإيرادات',
      fields: ['رقم الحجز', 'السعر', 'العميل', 'تاريخ الحجز', 'الحالة']
    },
    { 
      value: 'companies', 
      label: 'الشركات', 
      icon: Building2,
      description: 'تحليل أداء الشركات والعملاء المؤسسيين',
      fields: ['الاسم', 'العنوان', 'رقم الهاتف', 'شعار الشركة']
    },
    { 
      value: 'countries', 
      label: 'الدول والأسواق', 
      icon: Globe,
      description: 'تحليل التوزيع الجغرافي والأسواق',
      fields: ['الاسم العربي', 'الاسم الإنجليزي', 'العملة الافتراضية', 'أيام العطل']
    },
    { 
      value: 'currencies', 
      label: 'العملات والتسعير', 
      icon: DollarSign,
      description: 'تحليل أسعار الصرف والهوامش المالية',
      fields: ['الرمز', 'الاسم', 'معدل التحويل', 'هامش الربح']
    },
    { 
      value: 'chatwoot_contacts', 
      label: 'اتصالات Chatwoot', 
      icon: MessageSquare,
      description: 'تحليل تفاعلات العملاء والمحادثات',
      fields: ['الاسم', 'الإيميل', 'الهاتف', 'نوع الاتصال', 'خصائص إضافية']
    },
    { 
      value: 'performance_metrics', 
      label: 'مقاييس الأداء', 
      icon: BarChart3,
      description: 'تحليل مؤشرات الأداء الرئيسية والإحصائيات',
      fields: ['نوع المقياس', 'القيمة', 'تاريخ القياس', 'الوحدة', 'الهدف']
    }
  ];

  // أنواع التحليل المتاحة
  const analysisTypeOptions = [
    { 
      value: 'trends', 
      label: 'تحليل الاتجاهات',
      description: 'اكتشاف الأنماط والاتجاهات الزمنية في البيانات'
    },
    { 
      value: 'performance', 
      label: 'تحليل الأداء',
      description: 'تقييم الأداء مقارنة بالأهداف والمعايير'
    },
    { 
      value: 'distribution', 
      label: 'تحليل التوزيع',
      description: 'فهم كيفية توزيع البيانات عبر الفئات المختلفة'
    },
    { 
      value: 'insights', 
      label: 'رؤى ذكية',
      description: 'استخراج رؤى عميقة وغير واضحة من البيانات'
    },
    { 
      value: 'recommendations', 
      label: 'التوصيات الذكية',
      description: 'تقديم توصيات قابلة للتنفيذ لتحسين الأداء'
    },
    { 
      value: 'forecasting', 
      label: 'التنبؤ والاستشراف',
      description: 'التنبؤ بالاتجاهات المستقبلية والسيناريوهات'
    },
    { 
      value: 'correlation', 
      label: 'تحليل الارتباط',
      description: 'اكتشاف العلاقات والارتباطات بين متغيرات مختلفة'
    },
    { 
      value: 'anomaly', 
      label: 'اكتشاف الشذوذ',
      description: 'تحديد القيم والأنماط غير الطبيعية'
    },
    { 
      value: 'segmentation', 
      label: 'تحليل التقسيم',
      description: 'تقسيم البيانات إلى مجموعات ذات خصائص متشابهة'
    },
    { 
      value: 'competitive', 
      label: 'التحليل التنافسي',
      description: 'مقارنة الأداء مع المعايير الصناعية'
    }
  ];

  const timeRangeOptions = [
    { value: 'last_7_days', label: 'آخر 7 أيام' },
    { value: 'last_30_days', label: 'آخر 30 يوم' },
    { value: 'last_3_months', label: 'آخر 3 أشهر' },
    { value: 'last_6_months', label: 'آخر 6 أشهر' },
    { value: 'last_year', label: 'آخر سنة' },
    { value: 'custom', label: 'فترة مخصصة' },
    { value: 'all_time', label: 'جميع البيانات' }
  ];

  const detailLevelOptions = [
    { value: 'basic', label: 'أساسي', description: 'ملخص سريع للنقاط الرئيسية' },
    { value: 'detailed', label: 'تفصيلي', description: 'تحليل شامل مع التفاصيل' },
    { value: 'comprehensive', label: 'شامل', description: 'تحليل عميق مع جميع الجوانب' }
  ];

  const runAnalysis = async () => {
    if (!config.dataSource || !config.analysisType) {
      toast({
        title: "خطأ",
        description: "يرجى اختيار مصدر البيانات ونوع التحليل",
        variant: "destructive",
      });
      return;
    }

    setIsAnalyzing(true);
    setAnalysisResult(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('ai-analytics', {
        body: {
          analysisType: config.analysisType,
          dataSource: config.dataSource,
          filters: {
            timeRange: config.timeRange,
            detailLevel: config.detailLevel,
            includeRecommendations: config.includeRecommendations,
            includeCharts: config.includeCharts,
            language: config.language,
            customPrompt: config.customPrompt,
            ...config.filters
          }
        },
      });

      if (error) {
        throw new Error(error.message || 'فشل في استدعاء وظيفة التحليل');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setAnalysisResult(data);
      
      toast({
        title: "تم التحليل",
        description: "تم إجراء التحليل الذكي بنجاح",
      });
    } catch (error) {
      console.error('Analysis error:', error);
      const errorMessage = error instanceof Error ? error.message : 'فشل في إجراء التحليل الذكي';
      
      setAnalysisResult({
        analysis: '',
        dataCount: 0,
        timestamp: new Date().toISOString(),
        error: errorMessage
      });
      
      toast({
        title: "خطأ في التحليل",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const saveConfig = () => {
    const newConfig = { ...config };
    setSavedConfigs(prev => [...prev, newConfig]);
    toast({
      title: "تم حفظ الإعدادات",
      description: "تم حفظ إعدادات التحليل بنجاح",
    });
  };

  const loadConfig = (savedConfig: AnalysisConfig) => {
    setConfig(savedConfig);
    toast({
      title: "تم تحميل الإعدادات",
      description: "تم تحميل إعدادات التحليل المحفوظة",
    });
  };

  const exportResults = () => {
    if (!analysisResult) return;
    
    const exportData = {
      analysis: analysisResult.analysis,
      config: config,
      timestamp: analysisResult.timestamp,
      dataCount: analysisResult.dataCount
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-analysis-${new Date().toISOString()}.json`;
    a.click();
    
    toast({
      title: "تم التصدير",
      description: "تم تصدير نتائج التحليل بنجاح",
    });
  };

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-7xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
          <Brain className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">التحليل الذكي المتقدم</h1>
          <p className="text-muted-foreground mt-1">
            تحليل شامل للبيانات باستخدام الذكاء الاصطناعي مع إعدادات متقدمة
          </p>
        </div>
      </div>

      <Tabs defaultValue="analysis" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="analysis" className="flex items-center gap-2">
            <Brain className="w-4 h-4" />
            التحليل
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            الإعدادات المتقدمة
          </TabsTrigger>
          <TabsTrigger value="saved" className="flex items-center gap-2">
            <Bookmark className="w-4 h-4" />
            الإعدادات المحفوظة
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            قوالب التحليل
          </TabsTrigger>
        </TabsList>

        <TabsContent value="analysis" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>إعدادات التحليل السريع</CardTitle>
                  <CardDescription>
                    اختر مصدر البيانات ونوع التحليل المطلوب
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">مصدر البيانات</label>
                    <Select value={config.dataSource} onValueChange={(value) => setConfig(prev => ({ ...prev, dataSource: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="اختر مصدر البيانات" />
                      </SelectTrigger>
                      <SelectContent>
                        {dataSourceOptions.map((option) => {
                          const IconComponent = option.icon;
                          return (
                            <SelectItem key={option.value} value={option.value}>
                              <div className="flex items-center gap-2">
                                <IconComponent className="w-4 h-4" />
                                <div>
                                  <div className="font-medium">{option.label}</div>
                                  <div className="text-xs text-gray-500">{option.description}</div>
                                </div>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">نوع التحليل</label>
                    <Select value={config.analysisType} onValueChange={(value) => setConfig(prev => ({ ...prev, analysisType: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="اختر نوع التحليل" />
                      </SelectTrigger>
                      <SelectContent>
                        {analysisTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <div>
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-gray-500">{option.description}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">الفترة الزمنية</label>
                    <Select value={config.timeRange} onValueChange={(value) => setConfig(prev => ({ ...prev, timeRange: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {timeRangeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">مستوى التفاصيل</label>
                    <Select value={config.detailLevel} onValueChange={(value) => setConfig(prev => ({ ...prev, detailLevel: value as any }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {detailLevelOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <div>
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-gray-500">{option.description}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="recommendations">تضمين التوصيات</Label>
                      <Switch
                        id="recommendations"
                        checked={config.includeRecommendations}
                        onCheckedChange={(checked) => setConfig(prev => ({ ...prev, includeRecommendations: checked }))}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Label htmlFor="charts">تضمين المخططات</Label>
                      <Switch
                        id="charts"
                        checked={config.includeCharts}
                        onCheckedChange={(checked) => setConfig(prev => ({ ...prev, includeCharts: checked }))}
                      />
                    </div>
                  </div>

                  <Button 
                    onClick={runAnalysis} 
                    disabled={isAnalyzing || !config.dataSource || !config.analysisType}
                    className="w-full"
                  >
                    {isAnalyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        جاري التحليل...
                      </>
                    ) : (
                      <>
                        <TrendingUp className="w-4 h-4 mr-2" />
                        بدء التحليل المتقدم
                      </>
                    )}
                  </Button>

                  <div className="flex gap-2">
                    <Button variant="outline" onClick={saveConfig} size="sm" className="flex-1">
                      <Bookmark className="w-4 h-4 mr-2" />
                      حفظ
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={exportResults} 
                      size="sm" 
                      className="flex-1"
                      disabled={!analysisResult}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      تصدير
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="lg:col-span-2">
              <Card className="min-h-96">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>نتائج التحليل الذكي</span>
                    {analysisResult && !analysisResult.error && (
                      <Button variant="ghost" size="sm" onClick={exportResults}>
                        <Share2 className="w-4 h-4" />
                      </Button>
                    )}
                  </CardTitle>
                  <CardDescription>
                    النتائج والرؤى المستخرجة من الذكاء الاصطناعي مع التوصيات العملية
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!analysisResult ? (
                    <div className="text-center py-12">
                      <Brain className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">لم يتم إجراء أي تحليل بعد</p>
                      <p className="text-sm text-gray-500">اختر مصدر البيانات ونوع التحليل لبدء العمل</p>
                      
                      <div className="mt-6 text-right">
                        <h4 className="font-semibold mb-3">مصادر البيانات المتاحة:</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {dataSourceOptions.slice(0, 6).map((source) => {
                            const IconComponent = source.icon;
                            return (
                              <div key={source.value} className="flex items-center gap-2 p-2 border rounded-lg">
                                <IconComponent className="w-4 h-4 text-blue-500" />
                                <span className="text-sm font-medium">{source.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {analysisResult.error ? (
                        <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            {analysisResult.error}
                          </AlertDescription>
                        </Alert>
                      ) : (
                        <>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">
                                عدد السجلات: {analysisResult.dataCount}
                              </Badge>
                              <Badge variant="outline">
                                {new Date(analysisResult.timestamp).toLocaleString('ar-SA')}
                              </Badge>
                              <Badge variant="outline">
                                {config.detailLevel === 'basic' ? 'أساسي' : 
                                 config.detailLevel === 'detailed' ? 'تفصيلي' : 'شامل'}
                              </Badge>
                            </div>
                          </div>
                          
                          <Separator />
                          
                          <div className="prose prose-sm max-w-none">
                            <div className="whitespace-pre-wrap text-gray-700 leading-relaxed" style={{direction: 'rtl'}}>
                              {analysisResult.analysis}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>الإعدادات المتقدمة للتحليل</CardTitle>
              <CardDescription>
                تخصيص تفاصيل التحليل والمرشحات المتقدمة
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="custom-prompt">توجيهات مخصصة للتحليل</Label>
                    <Textarea
                      id="custom-prompt"
                      placeholder="أدخل توجيهات إضافية للذكاء الاصطناعي..."
                      value={config.customPrompt}
                      onChange={(e) => setConfig(prev => ({ ...prev, customPrompt: e.target.value }))}
                      className="mt-2"
                      rows={4}
                    />
                  </div>
                  
                  <div>
                    <Label>لغة التحليل</Label>
                    <Select value={config.language} onValueChange={(value) => setConfig(prev => ({ ...prev, language: value as any }))}>
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ar">العربية</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label>مرشحات البيانات</Label>
                    <div className="mt-2 p-3 border rounded-lg">
                      <p className="text-sm text-gray-600 mb-3">
                        المرشحات المتاحة تعتمد على مصدر البيانات المختار:
                      </p>
                      
                      {config.dataSource && (
                        <div className="space-y-2">
                          {dataSourceOptions
                            .find(ds => ds.value === config.dataSource)
                            ?.fields.map((field, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <Filter className="w-4 h-4 text-gray-400" />
                              <span className="text-sm">{field}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {!config.dataSource && (
                        <p className="text-sm text-gray-500">
                          اختر مصدر البيانات أولاً لعرض المرشحات المتاحة
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <h4 className="font-semibold mb-3">خيارات التحليل المتقدمة</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <Label>تحليل الاتجاهات الزمنية</Label>
                      <Switch />
                    </div>
                    <p className="text-xs text-gray-600">
                      تحليل التغيرات عبر الوقت واكتشاف الأنماط الموسمية
                    </p>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <Label>المقارنات التنافسية</Label>
                      <Switch />
                    </div>
                    <p className="text-xs text-gray-600">
                      مقارنة الأداء مع المعايير الصناعية والمنافسين
                    </p>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <Label>التنبؤات المستقبلية</Label>
                      <Switch />
                    </div>
                    <p className="text-xs text-gray-600">
                      توقع الاتجاهات المستقبلية بناءً على البيانات التاريخية
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="saved" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>الإعدادات المحفوظة</CardTitle>
              <CardDescription>
                إدارة وتحميل إعدادات التحليل المحفوظة مسبقاً
              </CardDescription>
            </CardHeader>
            <CardContent>
              {savedConfigs.length === 0 ? (
                <div className="text-center py-8">
                  <Bookmark className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">لا توجد إعدادات محفوظة</p>
                  <p className="text-sm text-gray-500">احفظ إعدادات التحليل المفضلة لديك لاستخدامها لاحقاً</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {savedConfigs.map((savedConfig, index) => (
                    <div key={index} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">إعداد {index + 1}</h4>
                        <Button size="sm" variant="outline" onClick={() => loadConfig(savedConfig)}>
                          تحميل
                        </Button>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p>المصدر: {dataSourceOptions.find(ds => ds.value === savedConfig.dataSource)?.label}</p>
                        <p>النوع: {analysisTypeOptions.find(at => at.value === savedConfig.analysisType)?.label}</p>
                        <p>الفترة: {timeRangeOptions.find(tr => tr.value === savedConfig.timeRange)?.label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>قوالب التحليل الجاهزة</CardTitle>
              <CardDescription>
                قوالب معدة مسبقاً لسيناريوهات التحليل الشائعة
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-4 border rounded-lg cursor-pointer hover:bg-gray-50" 
                     onClick={() => setConfig({
                       ...config,
                       dataSource: 'clients',
                       analysisType: 'performance',
                       timeRange: 'last_30_days',
                       detailLevel: 'detailed'
                     })}>
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-5 h-5 text-blue-500" />
                    <h4 className="font-medium">تحليل أداء العملاء</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    تحليل شامل لأداء العملاء ومعدلات التحويل والاحتفاظ
                  </p>
                </div>
                
                <div className="p-4 border rounded-lg cursor-pointer hover:bg-gray-50"
                     onClick={() => setConfig({
                       ...config,
                       dataSource: 'employees',
                       analysisType: 'trends',
                       timeRange: 'last_3_months',
                       detailLevel: 'comprehensive'
                     })}>
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-green-500" />
                    <h4 className="font-medium">تحليل اتجاهات الموظفين</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    تحليل اتجاهات الحضور والأداء والإنتاجية للموظفين
                  </p>
                </div>
                
                <div className="p-4 border rounded-lg cursor-pointer hover:bg-gray-50"
                     onClick={() => setConfig({
                       ...config,
                       dataSource: 'bookings',
                       analysisType: 'forecasting',
                       timeRange: 'last_year',
                       detailLevel: 'detailed'
                     })}>
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-5 h-5 text-purple-500" />
                    <h4 className="font-medium">تنبؤات الحجوزات</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    التنبؤ باتجاهات الحجوزات والإيرادات المستقبلية
                  </p>
                </div>
                
                <div className="p-4 border rounded-lg cursor-pointer hover:bg-gray-50"
                     onClick={() => setConfig({
                       ...config,
                       dataSource: 'tasks',
                       analysisType: 'anomaly',
                       timeRange: 'last_30_days',
                       detailLevel: 'basic'
                     })}>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <h4 className="font-medium">اكتشاف المشاكل</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    اكتشاف التأخيرات والمشاكل في تنفيذ المهام
                  </p>
                </div>
                
                <div className="p-4 border rounded-lg cursor-pointer hover:bg-gray-50"
                     onClick={() => setConfig({
                       ...config,
                       dataSource: 'countries',
                       analysisType: 'distribution',
                       timeRange: 'all_time',
                       detailLevel: 'detailed'
                     })}>
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="w-5 h-5 text-indigo-500" />
                    <h4 className="font-medium">تحليل الأسواق الجغرافية</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    توزيع العملاء والإيرادات عبر المناطق الجغرافية
                  </p>
                </div>
                
                <div className="p-4 border rounded-lg cursor-pointer hover:bg-gray-50"
                     onClick={() => setConfig({
                       ...config,
                       dataSource: 'chatwoot_contacts',
                       analysisType: 'insights',
                       timeRange: 'last_30_days',
                       detailLevel: 'comprehensive'
                     })}>
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-5 h-5 text-orange-500" />
                    <h4 className="font-medium">رؤى تفاعل العملاء</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    تحليل أنماط تفاعل العملاء ورضا الخدمة
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}